import { GroupsOutlined, PersonAddAlt1Outlined, SmartToyOutlined, TravelExploreOutlined } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useEffect, useState } from 'react';
import { chipColor } from '../../theme';
import PhoneNews from '../Share/PhoneNews';
import './start.css';

const MODES = [
    { key: 'friends', title: 'Play with friends', detail: 'A room for 2 to 12. You pick the teams.', icon: <GroupsOutlined /> },
    { key: 'challenge', title: 'Challenge a friend', detail: 'One on one. Starts when they join.', icon: <PersonAddAlt1Outlined /> },
    { key: 'match', title: 'Find an opponent', detail: 'Play someone new online.', icon: <TravelExploreOutlined /> },
    { key: 'computer', title: 'Play the computer', detail: 'Start straight away.', icon: <SmartToyOutlined /> }
];

// Player counts the game can split into two or three equal teams.
const PLAYER_COUNTS = [2, 3, 4, 6, 8, 9, 10, 12];
const HAND_SIZES = [7, 6, 5, 4, 3];
const TIMERS = [
    { value: 0, label: 'Off' },
    { value: 30, label: '30 s' },
    { value: 60, label: '1 min' },
    { value: 120, label: '2 min' }
];

const readName = () => {
    try {
        return localStorage.getItem('playerName') || '';
    } catch (error) {
        return '';
    }
};

const Choice = ({ label, value, onChange, options, render }) => (
    <div className="start-field">
        <span className="start-label">{label}</span>
        <ToggleButtonGroup exclusive size="small" value={value} onChange={(e, next) => next !== null && onChange(next)} className="start-toggle">
            {options.map(option => (
                <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
                    {render ? render(option) : option.label}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    </div>
);

/**
 * The one place a game begins. `onStart(mode, values)` does the work; this only
 * collects what that kind of game needs.
 */
const StartGame = ({ open, onStart, onClose, onClassic }) => {
    const [mode, setMode] = useState('friends');
    const [values, setValues] = useState({
        name: readName(),
        roomName: '',
        noOfPlayers: 2,
        difficulty: 'medium',
        team: 'Blue',
        board: 'classic',
        turnSeconds: 0,
        hints: true,
        handSize: 7
    });

    // Remember the name between visits; it's asked for in most modes.
    useEffect(() => {
        try {
            if (values.name) {
                localStorage.setItem('playerName', values.name);
            }
        } catch (error) { /* private mode */ }
    }, [values.name]);

    const set = (key) => (value) => setValues(current => ({ ...current, [key]: value }));
    const needsName = mode !== 'friends';
    const ready = mode === 'friends' ? values.roomName.trim().length > 0 : values.name.trim().length > 0;
    const startLabel = { friends: 'Create room', challenge: 'Create challenge', match: 'Find an opponent', computer: 'Start game' }[mode];

    const colourOptions = ['Blue', 'Green', 'Red'].map(team => ({ value: team, label: team }));

    return (
        <Dialog open={Boolean(open)} fullWidth maxWidth="sm" PaperProps={{ className: 'start-paper' }}>
            <DialogTitle>Start a game</DialogTitle>
            <DialogContent>
                <PhoneNews place="start"/>
                <div className="start-modes" role="radiogroup" aria-label="Kind of game">
                    {MODES.map(option => (
                        <button
                            key={option.key}
                            type="button"
                            role="radio"
                            aria-checked={mode === option.key}
                            className={`start-mode${mode === option.key ? ' selected' : ''}`}
                            onClick={() => setMode(option.key)}
                        >
                            <span className="start-mode-icon">{option.icon}</span>
                            <span className="start-mode-title">{option.title}</span>
                            <span className="start-mode-detail">{option.detail}</span>
                        </button>
                    ))}
                </div>

                <div className="start-fields">
                    {mode === 'friends' && <div className="start-row">
                        <TextField label="Room name" size="small" value={values.roomName} onChange={e => set('roomName')(e.target.value)} fullWidth autoFocus />
                        <TextField select label="Players" size="small" value={values.noOfPlayers} onChange={e => set('noOfPlayers')(e.target.value)} sx={{ minWidth: 110 }}>
                            {PLAYER_COUNTS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </TextField>
                    </div>}

                    {needsName && <TextField label="Your name" size="small" value={values.name} onChange={e => set('name')(e.target.value)} fullWidth autoFocus inputProps={{ maxLength: 40 }} />}

                    {mode === 'computer' && <Choice
                        label="Difficulty"
                        value={values.difficulty}
                        onChange={set('difficulty')}
                        options={[{ value: 'easy', label: 'Easy' }, { value: 'medium', label: 'Medium' }, { value: 'hard', label: 'Hard' }]}
                    />}

                    {(mode === 'challenge' || mode === 'computer') && <Choice
                        label="Your colour"
                        value={values.team}
                        onChange={set('team')}
                        options={colourOptions}
                        render={(option) => <><span className="chip start-chip" style={{ '--c': chipColor(option.value) }} aria-hidden="true" />{option.label}</>}
                    />}

                    {mode !== 'match' && <Choice
                        label="Board"
                        value={values.board}
                        onChange={set('board')}
                        options={[{ value: 'classic', label: 'Classic' }, { value: 'shuffled', label: 'Shuffled' }]}
                    />}

                    {(mode === 'friends' || mode === 'challenge') && <Choice
                        label="Turn timer"
                        value={values.turnSeconds}
                        onChange={set('turnSeconds')}
                        options={TIMERS}
                    />}

                    {/* For everyone in the game, and fixed once it starts, so
                        nobody plays with help the others don't have. */}
                    {mode !== 'match' && <Choice
                        label="Highlights"
                        value={values.hints}
                        onChange={set('hints')}
                        options={[{ value: true, label: 'On' }, { value: false, label: 'Off' }]}
                    />}

                    {/* Fewer cards make a quicker game with less to plan from. */}
                    {mode !== 'match' && <Choice
                        label="Cards in hand"
                        value={values.handSize}
                        onChange={set('handSize')}
                        options={HAND_SIZES.map(n => ({ value: n, label: String(n) }))}
                    />}

                    <p className="start-note">
                        {mode === 'friends' && "You'll get a link to share, then pick teams once everyone's in."}
                        {mode === 'challenge' && "You'll get a link to send. The game starts the moment your friend accepts."}
                        {mode === 'match' && 'Everyone here plays the classic board with 1-minute turns and highlights on, so games stay fair and keep moving.'}
                        {mode === 'computer' && 'Easy plays at random, Medium blocks and builds, Hard hunts for double threats.'}
                        {values.board === 'shuffled' && mode !== 'match' && ' Shuffled deals a new board each game, with the two copies of every card kept apart.'}
                    </p>
                </div>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                {onClassic && <Button onClick={onClassic} sx={{ mr: 'auto', color: 'text.secondary' }}>Use the classic look</Button>}
                {onClose && <Button onClick={onClose}>Cancel</Button>}
                <Button variant="contained" disabled={!ready} onClick={() => onStart(mode, { ...values, name: values.name.trim(), roomName: values.roomName.trim() })}>
                    {startLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StartGame;
